import { Component, Injector, Input, OnInit } from '@angular/core';

import { AccountsService, CheckResponseService, ObservationService, UtilsService } from '@services';
import { QuestionService } from '@modules/management/pages/details/check/services';
import { IQuestion, IResponse, IResponseAnswer, IResponseExternal, Response, USER_QUESTION_TYPES } from '@modules/management/pages/details/check/models';
import { formatUserAnswer } from '@services/accounts/user-filter';
import { checkDetailReport } from '@modules/reporting/models';
import { awaitHandler } from '@utils/awaitHandler';

import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { each, escape, filter, find, get, includes, isArray, isObject, map, round, trim } from 'lodash';
import { QuestionType } from '@modules/management/pages/details/check/services/question/model';

@Component({
  selector: 'app-check-questions',
  templateUrl: './check-questions.component.html',
  styleUrls: ['./check-questions.component.scss'],
})
export class CheckQuestionsComponent implements OnInit {
  @Input() responseId: string;

  public duration: string;
  public isResponseAvailable: boolean;
  public response: Response;
  public questionCompletedPercent = 0;
  // CVX-6533: pre-computed "Performed in MaintainX" panel; set only for external responses
  public externalPanel: {
    workOrder: string;
    completedAt: string;
    completedBy: string;
    score: string;
    fields: { label: string; value: string }[];
  };
  public dataTableOptions = {
    disableAutoResizeHandler: true,
    data: <IQuestion[]>[],
    paging: false,
    info: false,
    retrieve: true,
    stateSave: true,
    searching: false,
    autoWidth: false,
    disableScrolling: true,
    language: {},
    rowprop: 'data-mid',
    rowvaluefrom: 'questionID',
    enableSelectAll: false,
    sortClasses: false,
    ordering: false,
    columns: [],
    onRowClicked: (currentRow, event) => {
      const targetClass = trim(event.target.className);
      const answer = this.answers[currentRow.questionID];

      if (answer && targetClass === 'row-link') {
        this.checkDetailReport.openDetails(this.response, currentRow.questionID, {});
      }
    }
  };

  private columns = {
    title: {
      data: 'title',
      title: this.translate.instant('DASHPAGES.question-item'),
      render: (title: string, type, row, meta) => {
        return `${meta?.row + 1}. ${title}`;
      }
    },
    answer: {
      data: 'questionID',
      title: this.translate.instant('SHARED.Answer'),
      render: (id: string) => {
        const answerTitle = this.getAnswerTitle(id) || this.translate.instant('SHARED.NotCompleted');
        const answerStatus = this.getAnswerStatus(id);

        return `<span class="${answerStatus}">${answerTitle}</span>`;
      }
    },
    validation: {
      data: 'questionID',
      title: this.translate.instant('MGMT_DETAILS.Validation'),
      render: (id: string) => {
        const answer = this.answers[id];
        const title = this.getCellAnswerTitle(answer);
        let linkClass: string = '';

        if (this.observationService.getObservationById(answer?.observationID) || !answer?.observationID) {
          linkClass = 'row-link';
        }

        return title ? `<span class="${linkClass}">${this.translate.instant(title)}</span>` : null;
      }
    },
    duration: {
      data: 'questionID',
      title: this.translate.instant('SHARED.Duration'),
      render: (id: string) => {
        return moment.utc(this.answers[id]?.elapsedTime * 1000).format('mm:ss');
      }
    }
  };
  private columnsByStatus = {
    inProgress: [
      this.columns.title,
      this.columns.answer,
      this.columns.validation
    ],
    complete: [
      this.columns.title,
      this.columns.duration,
      this.columns.answer,
      this.columns.validation
    ]
  };
  private answers: { [answerId: string]: IResponseAnswer } = {};
  private questions: IQuestion[] = [];
  private questionLabelTypes = [QuestionType.PassFail, 'singleSelect', 'tolerance', 'actualMeasurement'];
  private checkDetailReport = new checkDetailReport(this.injector);

  constructor(
    private checkResponseService: CheckResponseService,
    private questionService: QuestionService,
    private translate: TranslateService,
    private injector: Injector,
    private observationService: ObservationService,
    private accountsService: AccountsService,
    private utils: UtilsService
  ) {}

  ngOnInit() {
    this.getQuestions();
  }

  private async getQuestions() {
    [this.response] = await awaitHandler(this.checkResponseService.getResponseAsync(this.responseId));

    if (this.response?.external) {
      // performed in MaintainX: there are no Corvex questions to tabulate
      this.externalPanel = this.buildExternalPanel(this.response.external);
      return;
    }

    this.isResponseAvailable = includes(['inProgress', 'complete'], this.response.status);
    this.dataTableOptions.columns = this.columnsByStatus[this.response.status];

    if (this.isResponseAvailable) {
      this.answers = this.response?.answers || {};
      this.questions = filter(map(this.response?.questions, (questionId) => {
        return this.questionService.getQuestion(questionId);
      }));
      this.dataTableOptions.data = this.questions;
      this.defineCompletedQuestions();
      this.defineDuration();
    }
  }

  private defineCompletedQuestions() {
    let answeredCount = 0;

    each(this.questions, (question) => {
      if (this.answers[question.questionID]) {
        answeredCount++;
      }
    });

    this.questionCompletedPercent = round(answeredCount / this.questions.length * 100) || 0;
  }

  private getAnswerTitle(questionId: string) {
    const question = this.questionService.getQuestion(questionId);
    const currentAnswer = this.answers[questionId] || {} as IResponseAnswer;
    const questionOptions = this.questionService.getQuestionOptions(question.questionID);
    let answerTitle = '';

    if (question) {
      if (includes(this.questionLabelTypes, question.type)) {
        const statusMap = {
          negative: this.translate.instant('MGMT_DETAILS.Fail'),
          neutral: this.translate.instant('SHARED.Neutral'),
          positive: this.translate.instant('MGMT_DETAILS.Pass')
        };

        answerTitle = statusMap[currentAnswer.result];
      } else if (question.type === 'multiSelect') {
        each(currentAnswer.response, (optionId: string) => {
          const currentOption = questionOptions[optionId];
          if (currentOption) {
            answerTitle += `<li>${escape(currentOption?.label)}</li>`;
          }
        });
      } else if (question.type === QuestionType.TextInput && currentAnswer?.responseValue) {
        each(currentAnswer?.responseValue, (option: { optionId: string; value: string }) => {
          answerTitle += `<li>${escape(option?.value)}</li>`;
        });
      } else if (question.type === QuestionType.DropdownSelection) {
        const currentOption = find(questionOptions, { optionID: currentAnswer.response });

        if (currentOption) {
          answerTitle = escape(currentOption?.label);
        }
      } else if (includes(USER_QUESTION_TYPES, question.type)) {
        // the answer is a pid or pids; [] is N/A
        answerTitle = escape(formatUserAnswer(currentAnswer.response, (userID) => this.accountsService.getByID(userID),
          this.translate.instant('MGMT_DETAILS.User_Disabled'), this.translate.instant('MGMT_DETAILS.User_Unknown')));
        if (!answerTitle && this.answers[questionId]) {
          answerTitle = this.translate.instant('DASHPAGES.NA');
        }
      } else if (question.type === QuestionType.IncrementEntry) {
        const value = currentAnswer?.responseValue?.[0]?.value;

        if (value) {
          answerTitle = escape(value);
        }
      }
    }

    return answerTitle;
  }

  private getAnswerStatus(questionId: string) {
    const question = this.questionService.getQuestion(questionId);
    let status = this.getAnswerTitle(questionId) ? '' : 'question-status';

    if (includes(this.questionLabelTypes, question?.type)) {
      status = `question-status ${this.answers[questionId]?.result}`;
    }

    return status;
  }

  private getCellAnswerTitle(answer: IResponseAnswer) {
    const observationId = get(answer, 'observationID');
    let linkName = '';

    if (observationId) {
      linkName = this.observationService.getObservationById(observationId) ? 'MGMT_DETAILS.View_Corrective_Action' : 'MGMT_DETAILS.Corrective_Action_Pending';
    } else if (get(answer, 'flaggedIssue')) {
      linkName = 'MGMT_DETAILS.View_Issue';
    } else if (get(answer, 'result') && (answer.notes?.length || answer.images?.length)) {
      linkName = 'MGMT_DETAILS.View_Validation';
    }

    return linkName;
  }

  private buildExternalPanel(external: IResponseExternal) {
    const hasScore = external.score !== '' && external.score !== undefined && external.score !== null;

    return {
      workOrder: `#${external.workOrderId ?? ''} ${external.title ?? ''}`.trim(),
      completedAt: this.utils.dateTimeFormat(external.completedAt, ''),
      completedBy: external.completer?.name || external.completer?.email || '',
      score: hasScore ? `${external.score}${external.maxScore !== '' && external.maxScore !== undefined ? ` / ${external.maxScore}` : ''}` : '',
      fields: map(isArray(external.fields) ? external.fields : [], (field) => ({
        label: field?.label ?? '',
        value: this.externalFieldValue(field?.value)
      }))
    };
  }

  // MaintainX field values are arbitrary JSON: show scalars as-is, dig the obvious value out of objects
  private externalFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (isArray(value)) {
      return map(value, (item) => this.externalFieldValue(item)).filter((item) => item !== '').join(', ');
    }
    if (isObject(value)) {
      const candidate = (value as any).inspectionStatus ?? (value as any).value;
      return candidate !== undefined && !isObject(candidate) ? String(candidate) : JSON.stringify(value);
    }
    return String(value);
  }

  private defineDuration() {
    if (this.response.completionTime) {
      const startTime = moment(this.response.claimTime);
      const completionTime = moment(this.response.completionTime);
      this.duration = moment.utc(completionTime.diff(startTime) * 1000).format('mm:ss');
    }
  }

}
